import {supabase} from "@/lib/supabase";
import {getRowsFiltered, ListParams, ListResponse} from "@/api/index";
import {Checklist, ChecklistTemplate, ListChecklist} from "@/types/Checklist";
import OrderDialog from "@/components/dialogs/OrderDialog.vue";
import {getCurrentUser, useGlobalSetting, useMainPopup} from "@/lib/utils";
import ChecklistFillForm from "@/components/checklists/ChecklistFillForm.vue";
import {AssetFetchParams, AssetFetchResult} from "@/types/Asset";

//TODO: global loading hinzufügen

/**
 * Get paginated and filtered orders
 */
export async function getChecklistsFiltered(params: ListParams): Promise<ListResponse<ListChecklist>> {
    return getRowsFiltered(params, "checklists", "id, title, description, assignees, order_id, asset_id, scheduled_start, state, items, locked_by")
}

export async function getCompressedChecklists(params: AssetFetchParams, baseFilter: Record<string, any>): Promise<AssetFetchResult> {
    const {
        offset = 0,
        limit = 100,
        search = ""
    } = params;

    const {data, error} = await supabase.rpc('filter_rows', {
        p_table: "checklists",
        p_filters: baseFilter,
        p_sort_column: 'id',
        p_sort_order: 'asc',
        p_limit: limit,
        p_offset: offset,
        p_select: "id, title, order_id, asset_id, scheduled_start, state",
        p_search: search,
        p_search_columns: ['id', 'title', 'description']
    })

    if (error) throw error

    return {
        items: (data?.items || []).map((d: any) => ({
            title: `#${d.id} - ${d.title}`,
            subtitle: '',
            chip: d.state,
            chip_color: `${d.state.toString().toLowerCase()}_color`,
            id: d.id
        })),
        total: data?.total || 0,
    }
}


export async function saveChecklistTemplate(template: ChecklistTemplate) {
    if (template.id) {
        // Update
        const {error} = await supabase
            .from('checklist_templates')
            .update({
                title: template.title,
                description: template.description,
                work_group: template.work_group || null,
                items: template.items
            })
            .eq('id', template.id)

        if (error) {
            console.error('Update failed:', error)
            return
        }
    } else {
        // Create
        const {error} = await supabase
            .from('checklist_templates')
            .insert({
                title: template.title,
                description: template.description,
                work_group: template.work_group || null,
                items: template.items
            })

        if (error) {
            console.error('Create failed:', error)
            return
        }
    }
}

export async function deleteChecklistTemplate(template: ChecklistTemplate | null) {
    if (!template?.id) return

    const {error} = await supabase
        .from('checklist_templates')
        .delete()
        .eq('id', template.id)

    if (error) {
        console.error('Delete failed:', error)
        return
    }
}

export async function assignChecklistTemplate(template: ChecklistTemplate | null, data: {
    templateId: number,
    assignees: string[],
    scheduledStart: string | null,
    scheduledEnd: string | null,
    assetId: string | null,
    orderId: number | null
    outage_code: number | null
}) {
    if (!template) return

    console.log(data.outage_code)

    const payload = {
        template_id: template.id,
        title: template.title,
        work_group: template.work_group || 'TDE-PM3',
        assignees: data.assignees,
        asset_id: data.assetId?.trim() || null,
        //TODO: checklist: order_id validieren, da sonst fehler wegen fkey in db
        order_id: data.orderId && !isNaN(data.orderId) ? data.orderId : null,
        outage_code: data.outage_code || null,
        scheduled_start: data.scheduledStart || null,
        scheduled_end: data.scheduledEnd || null,
        items: template.items,  // Kopie der Prüfpunkte (noch ohne ok/note)
        state: 'TERM'
    }

    //console.log('Checklist insert payload:', JSON.stringify(payload, null, 2))

    const {error} = await supabase.from('checklists').insert(payload)

    if (error) {
        console.error('Assign failed:', error)
        return
    }

    console.log(`Checkliste erstellt für ${data.assignees.length} User`)
}

// Lock erwerben (atomar via RPC, keine Race Condition)
export async function acquireChecklistLock(checklistId: number, username: string): Promise<boolean> {
    const {data, error} = await supabase.rpc('acquire_checklist_lock', {
        p_checklist_id: checklistId,
        p_username: username,
        p_timeout_minutes: 5
    })

    if (error) {
        console.error('Lock acquire failed:', error.message, error.code, error.details)
        return false
    }

    return data === true
}

export async function releaseChecklistLock(checklistId: number) {
    await supabase
        .from('checklists')
        .update({locked_by: null, locked_at: null})
        .eq('id', checklistId)
}

/**
 * Offene Checklisten des aktuellen Users laden (für Dashboard)
 */
export async function getMyOpenChecklists(): Promise<Checklist[]> {
    const username = getCurrentUser()
    if (!username) return []

    const {data, error} = await supabase
        .from('checklists')
        .select('id, title, description, work_group, assignees, order_id, asset_id, scheduled_start, scheduled_end, state, items, locked_by, locked_at, completed_by, completed_at')
        .contains('assignees', [username])
        .in('state', ['TERM', 'INBEARB', 'FREIGEGEBEN'])
        .order('scheduled_start', {ascending: true, nullsFirst: false})
        .limit(20)

    if (error) {
        console.error('getMyOpenChecklists failed:', error)
        return []
    }

    return (data || []) as Checklist[]
}

export async function openCheckListFillDialog(id: number) {
    useMainPopup().open(
        ChecklistFillForm,
        {
            checklistId: id,
        },
        {
            contentFilled: true,
            maxWidth: 1200,
            fullscreen: useGlobalSetting().isMobile,
            persistent: true,
        }
    );
}